<template>
  <el-row>
    <el-col :span="24">
      <h3 class="form-title">PSIT</h3>
    </el-col>
    <el-col :span="24" v-loading="loading">
      <div class="fs-sections">
        <div
          v-for="group in groups"
          :key="group.type"
          class="fs-section-card"
        >
          <h4 class="fs-section-title">{{ group.label }}</h4>

          <div
            v-for="slot in group.slots"
            :key="slot.key"
            class="fs-slot-row"
          >
            <el-tag v-if="slot.langLabel" size="small" class="fs-lang-tag">
              {{ slot.langLabel }}
            </el-tag>

            <div v-if="filesMap[slot.key]" class="fs-file-row">
              <div class="fs-file-info">
                <span class="fs-file-name">{{
                  filesMap[slot.key].file_name
                }}</span>
                <el-tag size="small" type="info">
                  .{{ filesMap[slot.key].file_extension }}
                </el-tag>
              </div>
              <div class="fs-file-actions">
                <el-button
                  type="primary"
                  @click="downloadFile(filesMap[slot.key])"
                >
                  Yuklab olish
                </el-button>
                <input
                  type="file"
                  :ref="(el) => setInputRef(el, slot.key)"
                  hidden
                  :accept="group.accept"
                  @change="(e) => uploadAndSave(e, slot)"
                />
                <el-button
                  :loading="saving[slot.key]"
                  @click="triggerUpload(slot.key)"
                >
                  Almashtirish
                </el-button>
              </div>
            </div>

            <div v-else class="fs-empty-row">
              <span class="fs-empty-text">Fayl yuklanmagan</span>
              <input
                type="file"
                :ref="(el) => setInputRef(el, slot.key)"
                hidden
                :accept="group.accept"
                @change="(e) => uploadAndSave(e, slot)"
              />
              <el-button
                type="primary"
                :loading="saving[slot.key]"
                @click="triggerUpload(slot.key)"
              >
                Fayl yuklash
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-col>
  </el-row>
</template>

<script setup>
import { ref, reactive, onMounted } from "vue";
import { ElMessage } from "element-plus";
import http from "@/http";
import store from "@/store";

const groups = [
  {
    type: "PSIT_METADATA",
    label: "Metadata",
    accept: ".pdf,.xls,.xlsx",
    slots: [{ key: "PSIT_METADATA:", type: "PSIT_METADATA", lang: "" }],
  },
  {
    type: "PUBLIC_SECTOR_DIAGRAM",
    label: "Public sector diagram",
    accept: "",
    slots: [
      {
        key: "PUBLIC_SECTOR_DIAGRAM:en",
        type: "PUBLIC_SECTOR_DIAGRAM",
        lang: "en",
        langLabel: "EN",
      },
      {
        key: "PUBLIC_SECTOR_DIAGRAM:uz_lat",
        type: "PUBLIC_SECTOR_DIAGRAM",
        lang: "uz_lat",
        langLabel: "UZ",
      },
    ],
  },
];

const allSlots = groups.flatMap((group) => group.slots);

const loading = ref(false);
const filesMap = reactive({});
const saving = reactive(
  allSlots.reduce((acc, slot) => ({ ...acc, [slot.key]: false }), {})
);
const inputRefs = {};

const FILE_URL = process.env.VUE_APP_BASE_API_URL + "api/v2/info/file/";

const setInputRef = (el, key) => {
  if (el) inputRefs[key] = el;
};

const triggerUpload = (key) => {
  inputRefs[key]?.click();
};

const fetchFiles = () => {
  loading.value = true;
  http
    .get("api/v1/financial-statistic-psit")
    .then(({ data }) => {
      data.forEach((item) => {
        filesMap[`${item.type}:${item.lang}`] = item;
      });
    })
    .catch(() => {
      ElMessage.error("Ma'lumot olishda xatolik");
    })
    .finally(() => {
      loading.value = false;
    });
};

const downloadFile = (file) => {
  const link = document.createElement("a");
  link.href = FILE_URL + file.file_id;
  link.setAttribute("download", file.file_name || file.file_id);
  link.target = "_blank";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const uploadAndSave = (event, slot) => {
  const file = event.target.files[0];
  if (!file) return;
  if (file.size / 1024 / 1024 > 20) {
    ElMessage.error("Fayl xajmi max. 20MB bo'lishi kerak!");
    event.target.value = "";
    return;
  }
  const formData = new FormData();
  formData.append("file", file);
  saving[slot.key] = true;
  http
    .post(`${process.env.VUE_APP_BASE_API_URL}api/v2/info/file/upload`, formData, {
      headers: {
        Authorization: store.state.accessToken,
        "Content-Type": "multipart/form-data",
      },
    })
    .then(({ data }) => {
      const payload = { type: slot.type, file_id: data.hashId };
      if (slot.type === "PUBLIC_SECTOR_DIAGRAM") payload.lang = slot.lang;
      return http({
        url: "api/v1/financial-statistic-psit",
        method: "put",
        data: payload,
      });
    })
    .then(({ data }) => {
      filesMap[`${data.type}:${data.lang}`] = data;
      ElMessage.success("Muvaffaqqiyatli saqlandi");
    })
    .catch(() => {
      ElMessage.error("Saqlashda xatolik. Qayta urinib ko'ring!");
    })
    .finally(() => {
      saving[slot.key] = false;
      event.target.value = "";
    });
};

onMounted(fetchFiles);
</script>

<style scoped lang="scss">
.fs-sections {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.fs-section-card {
  background: #fff;
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 24px;
}

.fs-section-title {
  font-size: 15px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 16px;
}

.fs-slot-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fs-slot-row + .fs-slot-row {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed #ebeef5;
}

.fs-lang-tag {
  flex-shrink: 0;
}

.fs-file-row {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
}

.fs-file-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.fs-file-name {
  font-size: 14px;
  font-weight: 500;
  color: #303133;
}

.fs-file-actions {
  display: flex;
  gap: 8px;
}

.fs-empty-row {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #909399;
}

.fs-empty-text {
  font-size: 14px;
}
</style>
